import React, { useCallback, useEffect, useRef, useState } from "react";
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from "@react-google-maps/api";
import { fetchFleetRows } from "utils/fleetRoster";
import { readToken } from "utils/session";
import { PUBLIC_ASSET_ICON } from "components/AccountImage";
import { MAP_FALLBACK_CENTER, pointPosition } from "components/RoutePlot";

// 1. Keep the unified libraries array
export const libraries = ["geometry", "drawing"];

function deviceTime(value) {
  if (value == null || value === "") return "-";
  return String(value).replace(/ GMT$/, "");
}

function pointLatLng(point) {
  return pointPosition(point);
}

function fitFleet(map, points) {
  if (!map || !window.google) return;
  const coords = (points || []).map(pointLatLng).filter(Boolean);
  if (!coords.length) return;
  if (coords.length === 1) {
    map.setCenter(coords[0]);
    map.setZoom(15);
    return;
  }
  const bounds = new window.google.maps.LatLngBounds();
  coords.forEach((coord) => bounds.extend(coord));
  map.fitBounds(bounds, 48);
}

const INITIAL_CENTER = MAP_FALLBACK_CENTER;

const FleetMap = () => {
  const [mapData, setMapData] = useState([]);
  const [selected, setSelected] = useState(null);
  const mapRef = useRef(null);
  const USERKEY = readToken();
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const carWidth = 30;
  const carHeight = 50;

  // 2. Use the modern hook instead of <LoadScript>
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: libraries,
    version: "3.64",
  });

  useEffect(() => {
    if (!USERKEY) return;
    fetchFleetRows({ apiBaseUrl, userKey: USERKEY })
      .then((rows) => setMapData(rows))
      .catch((error) => console.error(error));
  }, [USERKEY, apiBaseUrl]);

  const mapContainerStyle = {
    height: "500px",
    width: "100%",
  };

  const frameFleet = useCallback((map) => fitFleet(map, mapData), [mapData]);

  useEffect(() => {
    frameFleet(mapRef.current);
  }, [frameFleet, isLoaded]);

  // 3. Block rendering until the Google object is truly ready
  if (!isLoaded) return <div className="flex h-full items-center justify-center">Loading map...</div>;

  return (
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      center={INITIAL_CENTER}
      zoom={5}
      onLoad={(map) => {
        mapRef.current = map;
        frameFleet(map);
      }}
    >
      {mapData.map((point, index) => {
        const position = pointLatLng(point);
        return window.google && position ? (
          <Marker
            key={point.did || index}
            position={position}
            onClick={() => setSelected(point)}
            icon={{
              url: point.AssetMapImg || PUBLIC_ASSET_ICON,
              scaledSize: new window.google.maps.Size(carWidth, carHeight),
              anchor: new window.google.maps.Point(carWidth / 2, carHeight / 2),
            }}
          />
        ) : null;
      })}
      {selected && window.google && pointLatLng(selected) && (
        <InfoWindow
          position={pointLatLng(selected)}
          onCloseClick={() => setSelected(null)}
          options={{ pixelOffset: new window.google.maps.Size(0, -20) }}
        >
          <div style={{ minWidth: 200, color: "#1B254B", fontSize: 13, lineHeight: 1.45 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              {selected.did ?? "-"} / {selected.astid || "-"}
            </div>
            <div>Last update: {deviceTime(selected.lastupdt)}</div>
            <div>GSM: {selected.gsmss ?? "-"}</div>
            <div>Location: {selected.postype || "-"}</div>
            <div>Battery: {selected.batstat ?? "-"}%</div>
          </div>
        </InfoWindow>
      )}
    </GoogleMap>
  );
};

export default FleetMap;